import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
  Fade,
  Dialog,
} from '@mui/material';
import {
  Close,
  Edit,
  Cancel,
  Receipt,
  ShoppingCart,
  Add,
  Remove,
  AccessTime,
  ViewList,
  ViewModule,
  CheckCircle,
  Print,
  Refresh,
  ContentCopy,
  TimerOff,
  ErrorOutline,
  CheckCircleOutline,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useMenuStore } from '../../stores/menuStore.js';
import { useOrdersStore } from '../../stores/ordersStore.js';
import { useAuth } from '../../hooks/useAuth.js';
import { OrderService } from '../../services/orderService.js';
import { ReceiptPrinterService } from '../../services/receiptPrinterService.js';
import { BillingService } from '../../services/billingService.js';
import { QRbarService } from '../../services/qrbarService.js';

export const RightSidebar = () => {
  const { outletId, userId, outletName } = useAuth();
  const { cart, addToCart, decrementFromCart, clearCart } = useMenuStore();
  const { activeOrders, fetchActiveOrders, isLoading: ordersLoading } = useOrdersStore();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // 'edit' | 'cancel' | 'bill' | 'confirm'
  const [activeOrdersLayout, setActiveOrdersLayout] = useState('grid');
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [printStatus, setPrintStatus] = useState(null); // null | 'printing' | 'done' | 'error'
  const [closeCountdown, setCloseCountdown] = useState(null);
  const closeTimerRef = useRef(null);
  const countdownRef = useRef(null);

  // ── Billing / QR inline state ──────────────────────────────────
  const [billingActive, setBillingActive] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null); // 'creating' | 'active' | 'fetched' | 'payment_initiated' | 'paid' | 'expired' | 'error'
  const [expiresIn, setExpiresIn] = useState(0);
  const [billingError, setBillingError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [qrbarConnected, setQrbarConnected] = useState(false);
  const [qrFullscreen, setQrFullscreen] = useState(false);
  const pollRef = useRef(null);
  const billingTimerRef = useRef(null);

  // Cart totals
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // ── Place / Update Order ──────────────────────────────────────────
  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;
    setIsPlacingOrder(true);
    try {
      if (editingOrderId) {
        await OrderService.markOrderUpdated(editingOrderId);
      }

      await OrderService.createOrder({
        cart_list: cart.map(item => ({
          Product_ID: item.Product_ID || item.itemID,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        user_id: userId,
        outlet_id: outletId,
        status: 'Active',
      });

      clearCart();
      setEditingOrderId(null);
      if (outletId) fetchActiveOrders(outletId);
    } catch (error) {
      console.error('Error placing order:', error);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  // ── Edit Order ────────────────────────────────────────────────────
  const handleEditOrder = async (order) => {
    setActionLoading('edit');
    try {
      const items = await OrderService.fetchOrderItems(order.OrderID);
      clearCart();
      items.forEach(item => {
        const unitPrice =
          item.quantity > 0 ? item.amount / item.quantity : item.amount;
        addToCart(
          {
            itemID: item.item_id,
            Product_ID: item.item_id,
            name: item.item_name,
            price: unitPrice,
          },
          item.quantity
        );
      });
      setEditingOrderId(order.OrderID);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Error loading order for edit:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Cancel Order ──────────────────────────────────────────────────
  const handleCancelOrder = async (order) => {
    setActionLoading('cancel');
    try {
      await OrderService.updateOrderStatus(order.OrderID, 'Cancelled');
      setSelectedOrder(null);
      setOrderCompleted(false);
      if (outletId) fetchActiveOrders(outletId);
    } catch (error) {
      console.error('Error cancelling order:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Confirm Payment (Mark Paid) ────────────────────────────────────
  const handleConfirmPayment = async (order) => {
    setActionLoading('confirm');
    try {
      await OrderService.updateOrderStatus(order.OrderID, 'Completed');
      setOrderCompleted(true);
    } catch (error) {
      console.error('Error completing order:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Auto-close timer ───────────────────────────────────────────────
  const startCloseTimer = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    setCloseCountdown(10);
    countdownRef.current = setInterval(() => {
      setCloseCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    closeTimerRef.current = setTimeout(() => {
      handleDeselectOrder();
    }, 10000);
  };

  const handleDeselectOrder = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    cleanupBilling();
    setSelectedOrder(null);
    setOrderCompleted(false);
    setPrintStatus(null);
    setCloseCountdown(null);
    if (outletId) fetchActiveOrders(outletId);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // ── Print Receipt ──────────────────────────────────────────────────
  const handlePrintReceipt = async () => {
    setPrintStatus('printing');
    try {
      await ReceiptPrinterService.printReceipt(selectedOrder, outletName);
      setPrintStatus('done');
      startCloseTimer();
    } catch (err) {
      console.error('[RightSidebar] Print failed:', err);
      setPrintStatus('error');
      setTimeout(() => setPrintStatus(null), 3000);
    }
  };

  // ── Generate Bill (inline) ──────────────────────────────────────
  const handleGenerateBill = useCallback(async (order) => {
    if (!order?.OrderID || !outletId) return;

    setBillingActive(true);
    setSessionStatus('creating');
    setBillingError(null);
    setSessionData(null);
    setCopied(false);

    try {
      // 1. Check QRbar device health
      const health = await QRbarService.checkHealth();
      setQrbarConnected(health.connected);

      // 2. Create payment session
      const session = await BillingService.createPaymentSession(order.OrderID, outletId);
      setSessionData(session);
      setSessionStatus('active');

      // Calculate seconds until expiry
      if (session.expires_at) {
        const expiryTime = new Date(session.expires_at).getTime();
        const secondsLeft = Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));
        setExpiresIn(secondsLeft > 0 ? secondsLeft : 900);
      } else {
        setExpiresIn(900);
      }

      // 3. If QRbar device is connected, send QR data to it
      if (health.connected && session.qr_url) {
        await QRbarService.displayQR(session.qr_url);
      }
    } catch (err) {
      setBillingError(err.message || 'Failed to create payment session');
      setSessionStatus('error');
    }
  }, [outletId]);

  // ── Billing cleanup ─────────────────────────────────────────────
  const cleanupBilling = useCallback(async () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (billingTimerRef.current) clearInterval(billingTimerRef.current);

    // Expire session if still active
    if (
      sessionData?.session_token &&
      (sessionStatus === 'active' || sessionStatus === 'fetched' || sessionStatus === 'payment_initiated')
    ) {
      try { await BillingService.expireSession(sessionData.session_token); } catch {}
    }

    // Clear QRbar device
    if (qrbarConnected) {
      try { await QRbarService.clearDisplay(); } catch {}
    }

    setBillingActive(false);
    setSessionData(null);
    setSessionStatus(null);
    setExpiresIn(0);
    setBillingError(null);
    setCopied(false);
    setQrFullscreen(false);
  }, [sessionData?.session_token, sessionStatus, qrbarConnected]);

  // ── Billing countdown timer ────────────────────────────────────
  useEffect(() => {
    if (!billingActive) return;
    if (sessionStatus !== 'active' && sessionStatus !== 'fetched' && sessionStatus !== 'payment_initiated') {
      if (billingTimerRef.current) clearInterval(billingTimerRef.current);
      return;
    }

    billingTimerRef.current = setInterval(() => {
      setExpiresIn((prev) => {
        if (prev <= 1) {
          clearInterval(billingTimerRef.current);
          setSessionStatus('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => { if (billingTimerRef.current) clearInterval(billingTimerRef.current); };
  }, [billingActive, sessionStatus]);

  // ── Poll session status ────────────────────────────────────────
  useEffect(() => {
    if (!billingActive) return;
    if (sessionStatus !== 'active' && sessionStatus !== 'fetched' && sessionStatus !== 'payment_initiated') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }

    pollRef.current = setInterval(async () => {
      try {
        const sessions = await BillingService.getSessionStatuses(outletId);
        const mySession = sessions.find((s) => s.session_token === sessionData?.session_token);
        if (!mySession) return;

        const st = mySession.status?.toLowerCase();
        if (st === 'paid') {
          setSessionStatus('paid');
          clearInterval(pollRef.current);
          if (selectedOrder?.OrderID) {
            try { await OrderService.updateOrderStatus(selectedOrder.OrderID, 'Completed'); } catch {}
          }
          // Clear QRbar
          if (qrbarConnected) { try { await QRbarService.clearDisplay(); } catch {} }
          setOrderCompleted(true);
        } else if (st === 'expired') {
          setSessionStatus('expired');
          clearInterval(pollRef.current);
        } else if (st === 'payment_initiated') {
          setSessionStatus('payment_initiated');
        } else if (st === 'fetched') {
          setSessionStatus('fetched');
        }
      } catch {}
    }, 3000);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [billingActive, sessionStatus, sessionData?.session_token, outletId, selectedOrder?.OrderID, qrbarConnected]);

  // Cleanup billing refs on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (billingTimerRef.current) clearInterval(billingTimerRef.current);
    };
  }, []);

  const formatCountdown = (seconds) => {
    if (!seconds || seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleCopyUrl = () => {
    if (sessionData?.qr_url) {
      navigator.clipboard.writeText(sessionData.qr_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleManualPaymentReceived = async () => {
    setActionLoading('confirm');
    try {
      await OrderService.updateOrderStatus(selectedOrder.OrderID, 'Completed');
      setSessionStatus('paid');
      setOrderCompleted(true);
      if (qrbarConnected) { try { await QRbarService.clearDisplay(); } catch {} }
    } catch (err) {
      setBillingError('Failed to confirm payment');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusDisplay = () => {
    switch (sessionStatus) {
      case 'active': return { label: 'Waiting for customer to scan', color: '#FF9500', pulse: true };
      case 'fetched': return { label: 'Customer is viewing the bill', color: '#007AFF', pulse: true };
      case 'payment_initiated': return { label: 'Payment in progress...', color: '#34C759', pulse: true };
      case 'paid': return { label: 'Payment received!', color: '#34C759', pulse: false };
      case 'expired': return { label: 'Session expired', color: '#FF3B30', pulse: false };
      default: return { label: '', color: 'text.secondary', pulse: false };
    }
  };

  // ── Format time ──────────────────────────────────────────────────
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  // ── iOS-style thin scrollbar mixin ───────────────────────────────
  const thinScrollbar = {
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0, 0, 0, 0.10)', borderRadius: 2 },
    scrollbarWidth: 'thin',
    scrollbarColor: '#D1D1D6 transparent',
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 300 },
        minWidth: { xs: 'auto', md: 300 },
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {/* ═══ ACTIVE ORDERS WIDGET ═══ */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: selectedOrder ? '#4527A0' : 'rgba(255, 255, 255, 0.72)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '20px',
          border: '1px solid',
          borderColor: selectedOrder ? 'rgba(69, 39, 160, 0.3)' : 'rgba(0, 0, 0, 0.06)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
          transition: 'background-color 0.25s ease, border-color 0.25s ease',
        }}
      >
        {/* Header — hidden when an order is expanded */}
        {!selectedOrder && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: selectedOrder ? 'rgba(59, 130, 246, 0.12)' : 'rgba(0, 0, 0, 0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
            Active Orders
            {activeOrders.length > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 0.9,
                  py: 0.15,
                  borderRadius: '7px',
                  bgcolor: 'secondary.main',
                  color: 'rgba(255, 255, 255, 0.72)',
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  verticalAlign: 'middle',
                }}
              >
                {activeOrders.length}
              </Box>
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15 }}>
            <Tooltip title="List view" placement="top">
              <IconButton
                size="small"
                onClick={() => setActiveOrdersLayout('list')}
                aria-label="List view"
                sx={{
                  width: 28,
                  height: 28,
                  color: activeOrdersLayout === 'list' ? '#4527A0' : 'rgba(0, 0, 0, 0.15)',
                  bgcolor: activeOrdersLayout === 'list' ? 'rgba(69, 39, 160, 0.08)' : 'transparent',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'rgba(69, 39, 160, 0.06)',
                  },
                }}
              >
                <ViewList sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Grid view" placement="top">
              <IconButton
                size="small"
                onClick={() => setActiveOrdersLayout('grid')}
                aria-label="Grid view"
                sx={{
                  width: 28,
                  height: 28,
                  color: activeOrdersLayout === 'grid' ? '#4527A0' : 'rgba(0, 0, 0, 0.15)',
                  bgcolor: activeOrdersLayout === 'grid' ? 'rgba(69, 39, 160, 0.08)' : 'transparent',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'rgba(69, 39, 160, 0.06)',
                  },
                }}
              >
                <ViewModule sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => outletId && fetchActiveOrders(outletId)}
              disabled={ordersLoading}
              sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
            >
              {ordersLoading ? (
                <CircularProgress size={14} sx={{ color: 'text.disabled' }} />
              ) : (
                <AccessTime sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Box>
        </Box>
        )}

        {/* Scrollable Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, ...thinScrollbar }}>
          {selectedOrder ? (
            /* ─── Order Detail View ─── */
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100%' }}>
              {/* Sticky Header — Order title + Edit + Close */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pb: 1.5,
                  flexShrink: 0,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#FFFFFF' }}>
                  Order #{selectedOrder.OrderNum}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                  {!orderCompleted && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditOrder(selectedOrder)}
                      disabled={!!actionLoading}
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: 'rgba(255, 255, 255, 0.12)',
                        color: '#FFFFFF',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                      }}
                    >
                      {actionLoading === 'edit' ? <CircularProgress size={12} /> : <Edit sx={{ fontSize: 14 }} />}
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={handleDeselectOrder}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.12)',
                      color: '#FFFFFF',
                      width: 28,
                      height: 28,
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                    }}
                  >
                    <Close sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Scrollable Items list */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  borderRadius: '14px',
                  border: '1px solid',
                  borderColor: 'rgba(59, 130, 246, 0.15)',
                  bgcolor: 'rgba(255,255,255,0.7)',
                  ...thinScrollbar,
                }}
              >
                {(selectedOrder.ItemNames || []).map((name, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 1.5,
                      py: 1,
                      borderBottom:
                        idx < selectedOrder.ItemNames.length - 1
                          ? '1px solid rgba(59, 130, 246, 0.08)'
                          : 'none',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
                      {name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.82rem' }}
                    >
                      ×{selectedOrder.Quantity?.[idx] || 1}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Sticky Footer: Total + Action Buttons / Billing */}
              <Box sx={{ flexShrink: 0, pt: 1.5 }}>
                {/* Total */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1.5,
                    px: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
                    Total
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#FFFFFF' }}>
                    ₹{(selectedOrder.Amount || 0).toFixed(2)}
                  </Typography>
                </Box>

                {orderCompleted ? (
                  /* ── Order Completed State ── */
                  <Box>
                    <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                      <CheckCircle sx={{ fontSize: 28, color: '#34C759', mb: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#34C759', fontSize: '0.78rem' }}>
                        Payment Confirmed
                      </Typography>
                    </Box>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Print sx={{ fontSize: 16 }} />}
                      onClick={handlePrintReceipt}
                      disabled={printStatus === 'printing'}
                      sx={{
                        borderRadius: '10px',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        py: 0.85,
                        bgcolor: printStatus === 'done' ? '#1D1D1F' : printStatus === 'error' ? '#FF3B30' : '#1D1D1F',
                        '&:hover': { bgcolor: printStatus === 'done' ? '#1D1D1F' : printStatus === 'error' ? '#D32F2F' : '#1D1D1F' },
                      }}
                    >
                      {printStatus === 'printing' ? 'Printing...' : printStatus === 'done' ? 'Print Again' : printStatus === 'error' ? 'Print Failed — Retry' : 'Print Receipt'}
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleDeselectOrder}
                      sx={{
                        mt: 0.75,
                        borderRadius: '10px',
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        py: 0.75,
                        color: '#FF3B30',
                        borderColor: 'rgba(255, 59, 48, 0.12)',
                        '&:hover': { borderColor: '#FF3B30', bgcolor: 'rgba(255,59,48,0.04)' },
                      }}
                    >
                      {closeCountdown != null && closeCountdown > 0 ? `Close (${closeCountdown}s)` : 'Close'}
                    </Button>
                  </Box>
                ) : billingActive ? (
                  /* ── Inline Billing / QR Section ── */
                  <Box>
                    {/* Creating Session */}
                    {sessionStatus === 'creating' && (
                      <Box sx={{ py: 3, textAlign: 'center' }}>
                        <CircularProgress size={28} sx={{ color: '#FFFFFF', mb: 1 }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500, fontSize: '0.78rem' }}>
                          Generating payment QR...
                        </Typography>
                      </Box>
                    )}

                    {/* Error */}
                    {sessionStatus === 'error' && (
                      <Box sx={{ py: 2, textAlign: 'center' }}>
                        <ErrorOutline sx={{ fontSize: 32, color: '#FF3B30', mb: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', mb: 0.5 }}>
                          Failed to generate QR
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                          {billingError}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Refresh sx={{ fontSize: 14 }} />}
                          onClick={() => handleGenerateBill(selectedOrder)}
                          sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.72rem', bordercolor: 'primary.main', color: 'primary.main' }}
                        >
                          Retry
                        </Button>
                      </Box>
                    )}

                    {/* Expired */}
                    {sessionStatus === 'expired' && (
                      <Fade in timeout={400}>
                        <Box sx={{ py: 2, textAlign: 'center' }}>
                          <TimerOff sx={{ fontSize: 32, color: '#FF9500', mb: 1 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', mb: 0.5 }}>
                            QR Code Expired
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Refresh sx={{ fontSize: 14 }} />}
                            onClick={() => handleGenerateBill(selectedOrder)}
                            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.72rem', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                          >
                            Generate New QR
                          </Button>
                        </Box>
                      </Fade>
                    )}

                    {/* Paid */}
                    {sessionStatus === 'paid' && (
                      <Fade in timeout={600}>
                        <Box sx={{ py: 2, textAlign: 'center' }}>
                          <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'rgba(52,199,89,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
                            <CheckCircleOutline sx={{ fontSize: 30, color: '#34C759' }} />
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.5, color: '#FFFFFF' }}>
                            Payment Received!
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            ₹{(selectedOrder.Amount || 0).toFixed(2)} — Order #{selectedOrder.OrderNum}
                          </Typography>
                        </Box>
                      </Fade>
                    )}

                    {/* Active / Fetched / Payment Initiated — Show QR */}
                    {(sessionStatus === 'active' || sessionStatus === 'fetched' || sessionStatus === 'payment_initiated') && sessionData?.qr_url && (
                      <Fade in timeout={400}>
                        <Box sx={{ textAlign: 'center' }}>
                          {/* QR Code — tappable */}
                          <Box
                            onClick={() => setQrFullscreen(true)}
                            sx={{
                              display: 'inline-flex',
                              p: 1.5,
                              bgcolor: 'rgba(255, 255, 255, 0.72)',
                              borderRadius: '14px',
                              border: '2px solid #F2F2F7',
                              mb: 1.5,
                              cursor: 'pointer',
                              transition: 'transform 0.15s ease',
                              '&:hover': { transform: 'scale(1.03)' },
                              '&:active': { transform: 'scale(0.97)' },
                            }}
                          >
                            <QRCodeSVG
                              value={sessionData.qr_url}
                              size={140}
                              level="M"
                              bgColor="#FFFFFF"
                              fgColor="#1C1C1E"
                              style={{ display: 'block' }}
                            />
                          </Box>

                          {/* QRbar device indicator */}
                          <Typography variant="caption" sx={{ color: qrbarConnected ? '#34C759' : '#FF9500', fontSize: '0.65rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                            {qrbarConnected ? '● QRbar device connected' : '● No QRbar device connected'}
                          </Typography>

                          {/* Status Badge */}
                          <Box sx={{ mb: 1 }}>
                            <Chip
                              label={getStatusDisplay().label}
                              size="small"
                              sx={{
                                bgcolor: `${getStatusDisplay().color}18`,
                                color: getStatusDisplay().color,
                                fontWeight: 600,
                                fontSize: '0.68rem',
                                height: 24,
                                animation: getStatusDisplay().pulse ? 'pulse-opacity 2s ease-in-out infinite' : 'none',
                                '@keyframes pulse-opacity': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.6 } },
                              }}
                            />
                          </Box>

                          {/* Timer */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', fontFamily: 'monospace', color: expiresIn < 120 ? '#FF3B30' : '#1D1D1F' }}>
                              {formatCountdown(expiresIn)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                              remaining
                            </Typography>
                          </Box>

                          {/* Copy URL */}
                          <Button
                            size="small"
                            startIcon={<ContentCopy sx={{ fontSize: 12 }} />}
                            onClick={handleCopyUrl}
                            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.68rem', color: copied ? '#34C759' : '#86868B', textTransform: 'none', mb: 1 }}
                          >
                            {copied ? 'Copied!' : 'Copy link'}
                          </Button>

                          {/* Manual Payment */}
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={actionLoading === 'confirm' ? <CircularProgress size={12} /> : <CheckCircleOutline sx={{ fontSize: 14 }} />}
                            onClick={handleManualPaymentReceived}
                            disabled={!!actionLoading}
                            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.72rem', py: 0.6, color: '#34C759', borderColor: 'rgba(52, 199, 89, 0.15)', '&:hover': { borderColor: '#34C759', bgcolor: 'rgba(52,199,89,0.04)' } }}
                          >
                            Paid
                          </Button>
                        </Box>
                      </Fade>
                    )}
                  </Box>
                ) : (
                  /* ── Active Order Actions ── */
                  <Box>
                    {/* Mark Paid + Cancel row */}
                    <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={
                          actionLoading === 'confirm' ? (
                            <CircularProgress size={14} />
                          ) : (
                            <CheckCircle sx={{ fontSize: 16 }} />
                          )
                        }
                        onClick={() => handleConfirmPayment(selectedOrder)}
                        disabled={!!actionLoading}
                        sx={{
                          borderRadius: '10px',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          py: 0.85,
                          borderColor: 'rgba(52,199,89,0.25)',
                          bgcolor: 'rgba(255,255,255,0.6)',
                          color: '#34C759',
                          '&:hover': { borderColor: '#34C759', bgcolor: 'rgba(52,199,89,0.06)' },
                        }}
                      >
                        Paid
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={
                          actionLoading === 'cancel' ? (
                            <CircularProgress size={14} />
                          ) : (
                            <Cancel sx={{ fontSize: 16 }} />
                          )
                        }
                        onClick={() => handleCancelOrder(selectedOrder)}
                        disabled={!!actionLoading}
                        sx={{
                          borderRadius: '10px',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          py: 0.85,
                          borderColor: 'rgba(59, 130, 246, 0.25)',
                          bgcolor: 'rgba(255,255,255,0.6)',
                          color: '#FF3B30',
                          '&:hover': {
                            borderColor: '#FF3B30',
                            bgcolor: 'rgba(255,59,48,0.06)',
                          },
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>

                    {/* Generate Bill */}
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={
                        actionLoading === 'bill' ? (
                          <CircularProgress size={14} sx={{ color: 'rgba(255, 255, 255, 0.72)' }} />
                        ) : (
                          <Receipt sx={{ fontSize: 16 }} />
                        )
                      }
                      onClick={() => handleGenerateBill(selectedOrder)}
                      disabled={!!actionLoading}
                      sx={{
                        borderRadius: '10px',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        py: 1,
                        bgcolor: '#5B8DEF',
                        '&:hover': { bgcolor: '#4A7BE0' },
                      }}
                    >
                      Generate Bill
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          ) : activeOrders.length === 0 ? (
            /* ─── Empty state ─── */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <AccessTime sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500 }}>
                No active orders
              </Typography>
            </Box>
          ) : activeOrdersLayout === 'grid' ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 0.75,
                alignContent: 'start',
              }}
            >
              {activeOrders.map((order, idx) => (
                <Box
                  key={order.OrderID || `order-${idx}`}
                  onClick={() => setSelectedOrder(order)}
                  sx={{
                    p: 1,
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    minWidth: 0,
                    '&:hover': {
                      borderColor: 'rgba(0, 0, 0, 0.18)',
                      bgcolor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem' }} noWrap>
                    #{order.OrderNum}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.8rem', mt: 0.25 }}
                  >
                    ₹{(order.Amount || 0).toFixed(0)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      fontSize: '0.65rem',
                      display: 'block',
                      mt: 0.35,
                    }}
                  >
                    {formatTime(order.CreatedAt)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            /* ─── Orders List ─── */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {activeOrders.map((order, idx) => (
                <Box
                  key={order.OrderID || `order-${idx}`}
                  onClick={() => setSelectedOrder(order)}
                  sx={{
                    p: 1.5,
                    borderRadius: '14px',
                    border: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: 'rgba(0, 0, 0, 0.18)',
                      bgcolor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                      #{order.OrderNum}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.85rem' }}
                    >
                      ₹{(order.Amount || 0).toFixed(0)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.disabled',
                        fontSize: '0.72rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '65%',
                      }}
                    >
                      {(order.ItemNames || []).join(', ')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                      {formatTime(order.CreatedAt)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* ═══ CURRENT ORDER / CART WIDGET ═══ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'rgba(255, 255, 255, 0.72)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '20px',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ShoppingCart sx={{ fontSize: 17, color: 'text.secondary' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
              Current Order
            </Typography>
            {editingOrderId && (
              <Box
                sx={{
                  px: 0.75,
                  py: 0.15,
                  borderRadius: '6px',
                  bgcolor: '#FF9500',
                  color: 'rgba(255, 255, 255, 0.72)',
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                }}
              >
                EDITING
              </Box>
            )}
          </Box>
          {cart.length > 0 && (
            <Typography
              variant="caption"
              onClick={() => {
                clearCart();
                setEditingOrderId(null);
              }}
              sx={{
                color: '#FF3B30',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.72rem',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Clear
            </Typography>
          )}
        </Box>

        {/* Cart Items — Scrollable */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 0.5, ...thinScrollbar }}>
          {cart.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <ShoppingCart sx={{ fontSize: 28, color: 'text.disabled', mb: 0.75 }} />
              <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500, fontSize: '0.82rem' }}>
                Tap items to add
              </Typography>
            </Box>
          ) : (
            cart.map(item => (
              <Box
                key={item.itemID}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'rgba(0, 0, 0, 0.03)',
                }}
              >
                {/* Name + unit price */}
                <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: '0.82rem' }}
                    noWrap
                  >
                    {item.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                    ₹{item.price} × {item.quantity}
                  </Typography>
                </Box>

                {/* Qty controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <IconButton
                    size="small"
                    onClick={() => decrementFromCart(item.itemID)}
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: 'background.default',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.06)' },
                    }}
                  >
                    <Remove sx={{ fontSize: 13 }} />
                  </IconButton>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      minWidth: 20,
                      textAlign: 'center',
                      fontSize: '0.82rem',
                    }}
                  >
                    {item.quantity}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => addToCart(item, 1)}
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: 'background.default',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.06)' },
                    }}
                  >
                    <Add sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>

                {/* Line total */}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    ml: 1,
                    minWidth: 48,
                    textAlign: 'right',
                    fontSize: '0.82rem',
                  }}
                >
                  ₹{(item.price * item.quantity).toFixed(0)}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        {/* Cart Total + Place Order Button */}
        {cart.length > 0 && (
          <Box
            sx={{
              p: 1.5,
              borderTop: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.03)',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.25,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.82rem' }}>
                Total ({cartItemCount} item{cartItemCount !== 1 ? 's' : ''})
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '1rem' }}>
                ₹{cartTotal.toFixed(2)}
              </Typography>
            </Box>
            <Button
              fullWidth
              variant="contained"
              onClick={handlePlaceOrder}
              disabled={isPlacingOrder}
              startIcon={
                isPlacingOrder ? (
                  <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.72)' }} />
                ) : null
              }
              sx={{
                borderRadius: '14px',
                py: 1.2,
                fontWeight: 700,
                fontSize: '0.88rem',
                bgcolor: 'secondary.main',
                letterSpacing: '0.2px',
                '&:hover': { bgcolor: '#D70040' },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255, 59, 48, 0.15)',
                  color: 'rgba(255, 255, 255, 0.72)',
                },
              }}
            >
              {isPlacingOrder
                ? 'Placing...'
                : editingOrderId
                  ? 'Update Order'
                  : 'Place Order'}
            </Button>
          </Box>
        )}
      </Box>

      {/* ═══ QR Fullscreen Overlay ═══ */}
      <Dialog
        open={qrFullscreen}
        onClose={() => setQrFullscreen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            p: 3,
            textAlign: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.72)',
          },
        }}
      >
        <IconButton
          size="small"
          onClick={() => setQrFullscreen(false)}
          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.default', width: 32, height: 32 }}
        >
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
        {sessionData?.qr_url && (
          <Box sx={{ pt: 1 }}>
            <QRCodeSVG
              value={sessionData.qr_url}
              size={280}
              level="M"
              bgColor="#FFFFFF"
              fgColor="#1C1C1E"
              style={{ display: 'block', margin: '0 auto' }}
            />
            <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
              Scan to pay — Order #{selectedOrder?.OrderNum}
            </Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};
